import { writable } from 'svelte/store';
import { noop, safe_not_equal } from 'svelte/internal';
import { debounce } from '../enquiry/services/debounce';
const subscriber_queue = [];
/**
 * Create a lazy `Writable` store without default value that allows both updating and reading by subscription.
 * @param {StartStopNotifier=}start start and stop notifications for subscriptions
 */
export function lazyWritable(start = noop) {
    let value = undefined;
    let isInitialized = false;
    let stop;
    const subscribers = new Set();
    const update_queue = [];
    function set(new_value) {
        isInitialized = true;
        if (safe_not_equal(value, new_value)) {
            value = new_value;
            if (stop) { // store is ready
                const run_queue = !subscriber_queue.length;
                for (const subscriber of subscribers) {
                    subscriber[1]();
                    subscriber_queue.push(subscriber, value);
                }
                if (run_queue) {
                    for (let i = 0; i < subscriber_queue.length; i += 2) {
                        subscriber_queue[i][0](subscriber_queue[i + 1]);
                    }
                    subscriber_queue.length = 0;
                }
            }
        }
        if (update_queue.length) {
            for (let i = 0; i < update_queue.length; i++) {
                update_queue[i](value);
            }
            update_queue.length = 0;
        }
    }
    function update(fn) {
        if (isInitialized) {
            set(fn(value));
        }
        else {
            update_queue.push(fn);
        }
    }
    function subscribe(run, invalidate = noop) {
        const subscriber = [run, invalidate];
        subscribers.add(subscriber);
        if (subscribers.size === 1) {
            stop = start(set) || noop;
        }
        if (isInitialized) {
            run(value);
        }
        return () => {
            subscribers.delete(subscriber);
            if (subscribers.size === 0) {
                stop();
                stop = null;
            }
        };
    }
    return { set, update, subscribe };
}
/**
 * Create a state less store that only notifies subscriber about changes that occur after subscription.
 */
export function eventStore() {
    const subscribers = new Set();
    function set(value) {
        for (const subscriber of subscribers) {
            subscriber[1]();
            subscriber[0](value);
        }
    }
    function subscribe(run, invalidate = noop) {
        const subscriber = [run, invalidate];
        subscribers.add(subscriber);
        return () => {
            subscribers.delete(subscriber);
        };
    }
    return { set, subscribe };
}
/**
 * Create a store that debounces all writes
 */
export function debounceStore(value, wait) {
    const { set, subscribe, update } = writable(value);
    const debouncedSet = debounce(set, wait);
    return {
        set: debouncedSet,
        update(updater) {
            debouncedSet(updater(value));
        },
        subscribe
    };
}
